import type { Accessor } from 'solid-js';
import type { SetStoreFunction } from 'solid-js/store';
import { measureWidth, wrapText, MIN_TEXT_BOX } from './fitText';
import { cornerResizeCmd, fieldReserveCmd } from '../../project/commands';
import { AbstractTextImpl, type ITextImpl, type SetPosFn } from './AbstractTextImpl';
import type { HugFit, ResizeHandles, GeometrySeed, Dimensions } from '../interactions/ObjectImpl';
import { defaultSeedBox } from 'Shared/posters/canvas/interactions/ObjectImpl';
import type { ModeContext } from '../../project/PosterMode';
import type { Box, Position, RuntimeText } from '../../types';
import type { Style } from '../../project/posterSchema';
import type { ObjectContext } from '../../project/objects';
import type { ImplRegistry } from '../interactions/ObjectImpl';
import type { HistoryContext } from '../../project/history';
import { TemplateTextImpl, wrappedHeight } from 'Shared/posters/canvas/text/TemplateTextImpl';

//TODO use a start type for this - eg Box
type LiveHugBox = { width: number; height: number; left: number; top: number };

export class PlainWrap extends AbstractTextImpl implements ITextImpl
{
	static initialSeed(object: RuntimeText, layoutPos?: () => Position | undefined): GeometrySeed
	{
		return {
			box: {
				pos:  object.position ?? defaultSeedBox.pos,
				dims: { width: object.size?.width ?? defaultSeedBox.dims.width, height: defaultSeedBox.dims.height },
			},
			layoutPos,
		};
	}

	constructor(
		object:     RuntimeText,
		userFacing: Accessor<boolean>,
		canvas:     Dimensions,
		effStyle:   Accessor<Style | undefined>,
		lineHeight: Accessor<number>,
		textAlign:  Accessor<string>,
		textRef:    (() => SVGTextElement | null | undefined) | undefined,
		getContentRef: () => SVGGElement | undefined,
		private template: TemplateTextImpl,

		private wrapOverride:    Accessor<number | null>,
		private setWrapOverride: (v: number | null) => void,

		private resized:       Accessor<boolean>,
		private setResized:    (v: boolean) => void,
		private laidOutByTemplate: Accessor<boolean>,
		private frozenFont:    Accessor<number | null>,
		private setFrozenFont: (v: number | null) => void,
		private cornerBase:    { width: number | null; font: number },
		private editState:     { hasBeenEdited: boolean },

		box:    Box,
		setBox: SetStoreFunction<Box>,
		seed:   GeometrySeed,
		contentOffsetY:    Accessor<number>,
		setContentOffsetY: (v: number) => void,
	)
	{
		super(object, userFacing, canvas, effStyle, lineHeight, textAlign, textRef, getContentRef, box, setBox, seed, contentOffsetY, setContentOffsetY);
	}

	resizeHandles() { 
		return 'textCorner' as ResizeHandles; 
	}

	boxWidth() { 
		return this.wrapWidth() ?? undefined; 
	}

	fieldHeight() { return undefined; }

	getWrappedHeight(w: number) { 
		return this.userWrappedHeight(w); 
	}

	initialText() { 
		return this.displayText(); 
	}

	getText() { 
		return this.displayText(); 
	}

	// The drag only ran the live preview signals, so the final font/width come from those, not the store
	// (which still holds the pre-drag values that become this command's undo state).
	onCornerResizeEnd(box: Box, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void {
		this.cornerResizeEnd(box, this.wrapWidth(), this.wrapFontSize(), objCtx, impls, histCtx);
	}

	onWidthResize(w: number): void { 
		this.markResized(); 
		this.setWrapOverride(w);
	}

	onWidthResizeEnd(box: Box, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void {
		const width = this.wrapWidth();
		this.setWrapOverride(null);
		this.commitWidthResize(box, width, objCtx, impls, histCtx);
	}

	yOffset(): number { return 0 }
	onFieldWidthResize(_w: number): void {}
	onFieldWidthResizeEnd(_left: number): void {}

	overrideFontSize(): number | null { 
		return this.frozenFont() ?? this.template.designFont();
	}

	onTextEdit(text: string, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext) { 
		this.template.templateResize(this.editState, text, objCtx, impls, histCtx);
	}

	fitAfterEdit(_snap: Box, _setPos: SetPosFn, _coy: number, _setCoy: (v: number) => void, _objCtx: ObjectContext, _impls: ImplRegistry, _histCtx: HistoryContext, _canvas: Dimensions): boolean { return false }

	startFontSize()
	{
		this.markResized();
		this.cornerBase.width = this.wrapWidth();
		this.cornerBase.font  = this.wrapFontSize();
		return this.cornerBase.font;
	}

	onCornerResize(newFontSize: number, _objCtx: ObjectContext, _impls: ImplRegistry, _histCtx: HistoryContext)
	{
		const newWidth = this.cornerBase.width === null ? null : Math.round(this.cornerBase.width * newFontSize / this.cornerBase.font);
		this.cornerResize(newFontSize, newWidth);
	}

	getWrappedLines(_width: Accessor<number>)
	{
		const wrapped = this.wrapWidth() !== null || this.wrapFallbackWidth() !== undefined;
		if (!wrapped) 
			return undefined;
		return () => wrapText(this.displayText(), (this.wrapWidth() ?? this.wrapFallbackWidth()!) as number, { ...this.effStyle(), fontSize: this.wrapFontSize() });
	}

	getHugFit()
	{
		if (this.layoutPos?.() && !this.resized())
			return this.template.getHugFit();

		const base = this.hugContentMinSize(this.wrapWidth());
		if (base.measured === false || this.resized())
			return base;
		const result: HugFit = {
			...base,
			box: {
				pos:  { x: this.anchoredLeft(base.box.dims.width), y: this.anchoredTop(this.lineBoxHeight()) },
				dims: base.box.dims,
			},
		};
		return result;
	}

	// The size the shadow template lays this field out at. The field *draws* shrink-wrapped around its
	// ink (getSize), but that ink width can't be the layout's input: a container would then align the
	// glyph run rather than the field's reserved box, so a centred field spanning a left-aligned
	// container came out left-aligned. So the layout places the design width — exactly the box a
	// flex-managed field occupies in design mode — and displayBox() shrink-wraps the placed box again.
	// The height is the measured one either way: unlike its width, a field's height grows with the
	// record, and the layout has to reflow for it.
	override layoutSize(): Dimensions {
		const size  = this.getSize();
		const wrapW = this.wrapWidth() ?? this.wrapFallbackWidth();
		if (!this.laidOutByTemplate() || wrapW == null)
			return size;
		return { width: wrapW, height: size.height };
	}

	// The placed layout box (design width) → the box the field draws and selects: its ink, sitting where
	// textAlign puts it inside that width. Slack is measured off the box the layout handed back rather
	// than off wrapWidth() so a resized or unlinked field — laid out at its own size — converts to itself.
	override displayBox(laidOut: Box): Box {
		const ink   = this.getSize().width;
		const slack = laidOut.dims.width - ink;
		return {
			pos:  { x: laidOut.pos.x + this.alignFactor() * slack, y: laidOut.pos.y },
			dims: { width: ink, height: laidOut.dims.height },
		};
	}

	public displayText()
	{
		return this.template.displayText();
	}

	wrapWidth(): number | null {
		return this.wrapOverride() ?? this.object.size?.width ?? null;
	}

	private designTop(): number {
		return this.object.position?.y ?? 0;
	}

	// Export persists the design box position verbatim: object.position always holds it (a drag converts
	// its ink box to design coords at commit — see storedPositionOf), so re-exporting an untouched field
	// is a no-op and can never nudge it. Re-deriving design from the live hugged box here instead was not
	// idempotent — the valign inset (via the wrap-measured lineBoxHeight) shifts a few px across font-load
	// epochs — so every AutoSave crept every field. A resized field no longer hugs, so its box is stored.
	override storedTop(bboxTop: number): number {
		return this.resized() ? bboxTop : this.designTop();
	}

	override storedLeft(bboxLeft: number): number {
		return this.resized() ? bboxLeft : (this.object.position?.x ?? bboxLeft);
	}

	// The horizontal counterpart of verticalInset: how far the hugged ink box is inset from the design
	// box-left for the current textAlign (0 / half / all of the design-width-minus-ink slack for
	// left / center / right). Pairs with verticalInset to convert between the design and live boxes.
	private horizontalInset(): number {
		const width = this.wrapWidth() ?? this.wrapFallbackWidth();
		if (width == null)
			return 0;
		return this.alignFactor() * (width - this.getSize().width);
	}

	// How much of the horizontal slack sits to the left of the ink: none / half / all of it for a
	// left / centre / right aligned field. The one place textAlign becomes a number.
	private alignFactor(): number {
		const align = (this.effStyle()?.textAlign ?? 'center') as string;
		return align === 'right' ? 1 : align === 'center' ? 0.5 : 0;
	}

	// ink → design: strip the hug insets so a just-dragged live box is stored as its design box position.
	// A resized field's box is its stored box, so pass it through. The inverse of livePosition.
	override storedPositionOf(ink: Position): Position {
		if (this.resized())
			return ink;
		return { x: ink.x - this.horizontalInset(), y: ink.y - this.verticalInset(this.lineBoxHeight()) };
	}

	// design → ink: re-anchor a stored design position back to the live hugged box, so a move command's
	// undo/redo restores the on-screen position. The inverse of storedPositionOf (same insets, so exact).
	override livePosition(design: Position): Position {
		if (this.resized())
			return design;
		return { x: design.x + this.horizontalInset(), y: design.y + this.verticalInset(this.lineBoxHeight()) };
	}

	override storedWidth(bboxWidth: number): number | undefined {
		return this.resized() ? bboxWidth : this.object.size?.width;
	}

	private lineBoxHeight(): number {
		const width = this.wrapWidth() ?? this.wrapFallbackWidth();
		if (width == null)
			return this.minHeight();
		return this.userWrappedHeight(width);
	}

	// The downward shift that places the hugged line box within the field's reserved height per the
	// vertical alignment: 0 / half / all of the slack for top / center / bottom. Rounded once here (not
	// on the summed top) so anchoredTop and its inverse storedTop subtract the identical integer and the
	// save→reload round-trip is exact. Record-independent recovery: bboxTop and the inset both move with
	// the record's content height, so they cancel and storedTop returns the same design top for any record.
	private verticalInset(contentHeight: number): number {
		const align = this.object.alignment ?? 'top';
		if (align === 'top')
			return 0;
		const slack = (this.object.size?.height ?? this.minHeight()) - contentHeight;
		return align === 'bottom' ? Math.round(slack) : Math.round(slack / 2);
	}

	private anchoredTop(contentHeight: number): number {
		return this.designTop() + this.verticalInset(contentHeight);
	}

	private markResized() {
		if (!this.resized()) {
			this.setFrozenFont(this.template.designFont());
			this.setResized(true);
		}
	}

	private onFontChange(fontSize: number) {
		this.setFrozenFont(fontSize);
	}

	private wrapFallbackWidth() {
		return this.template.boxWidth();
	}

	// A drag frame updates the live preview signals only — the font and wrap width both render off them
	// (overrideFontSize / getWrappedLines), so the preview needs no store write. Writing the design store
	// per frame instead re-derived the geometry mid-gesture (and remounted the offscreen capture poster),
	// which made the hugged box flicker under the selection rect. cornerResizeEnd commits the one command.
	private cornerResize(newFontSize: number, newWrapWidth: number | null)
	{
		this.onFontChange(newFontSize);
		this.setWrapOverride(newWrapWidth);
	}

	private cornerResizeEnd(box: Box, newWidth: number | null, newFontSize: number, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext)
	{
		const { object } = this;
		const prevFontSize  = (object.style?.fontSize ?? 0) as number;
		const prevSizeWidth = object.size?.width ?? null;
		const prevPos       = { x: object.position?.x ?? 0, y: object.position?.y ?? 0 };
		this.setWrapOverride(null);
		histCtx.execute(cornerResizeCmd(objCtx, impls, object.id, prevFontSize, prevSizeWidth, prevPos, newFontSize, newWidth, { x: box.pos.x, y: box.pos.y }, true));
	}

	private commitWidthResize(box: Box, width: number | null, objCtx: ObjectContext, impls: ImplRegistry, histCtx: HistoryContext): void
	{
		const { object, effStyle, lineHeight } = this
		const font   = this.wrapFontSize()
		const w      = width ?? this.template.boxWidth()
		const height = wrappedHeight(this.displayText(), w, effStyle, lineHeight)
		histCtx.execute(fieldReserveCmd(objCtx, impls, object.id,
			{
				fontSize: (object.style?.fontSize as number | undefined) ?? null,
				box: {
					pos:  { x: object.position?.x ?? 0, y: object.position?.y ?? 0 },
					dims: { width: object.size?.width, height: object.size?.height },
				},
			},
			{ fontSize: font, box: { pos: { x: box.pos.x, y: box.pos.y }, dims: { width: w, height } } },
		))
	}

	private anchoredLeft(measuredWidth: number): number {
		const left  = this.object.position?.x ?? 0;
		const width = this.wrapWidth() ?? this.wrapFallbackWidth();
		if (width == null)
			return left;
		return left + this.alignFactor() * (width - measuredWidth);
	}

	private liveHugBox(): LiveHugBox | null
	{
		if (this.resized() || this.layoutPos?.()) 
			return null;

		const style = this.effStyle();
		const fs    = this.wrapFontSize();
		const wrapW = (this.wrapWidth() ?? this.wrapFallbackWidth()) as number;
		const lines = wrapText(this.displayText(), wrapW, { ...style, fontSize: fs });

		const width  = Math.max(MIN_TEXT_BOX, Math.ceil(Math.max(...lines.map(l => measureWidth(l, style, fs)))));
		const height = Math.max(MIN_TEXT_BOX, Math.ceil(lines.length * fs * this.lineHeight()));

		const left  = (this.box.pos.x ?? 0) + this.alignFactor() * ((this.box.dims.width ?? width) - width);
		const top   = this.box.pos.y ?? 0;
		return { width, height, left, top };
	}

	effectiveBox(userExists:boolean): Box
	{
		const hug  = this.liveHugBox();
		const base = hug
			? { pos: { x: hug.left, y: hug.top }, dims: { width: 0, height: hug.height } }
			: super.effectiveBox(userExists);
		if (this.layoutPos?.() || !this.userFacing())
			return base;
		return this.clampBoxIntoParent(base, this.canvas.width, this.canvas.height);
	}

	getBBox(userExists:boolean): Box
	{
		const hug = this.liveHugBox();
		if (!hug)
			return super.getBBox(userExists);
		const eb = this.effectiveBox(userExists);
		return { pos: eb.pos, dims: { width: hug.width, height: eb.dims.height } };
	}

	getHitBBox(userExists:boolean): Box
	{
		const hug = this.liveHugBox();
		if (!hug)
			return super.getHitBBox(userExists);
		const eb = this.effectiveBox(userExists);
		return { pos: { x: eb.pos.x, y: eb.pos.y + this.contentOffsetY() }, dims: { width: hug.width, height: eb.dims.height } };
	}

	getSize(): Dimensions
	{
		const hug = this.liveHugBox();
		if (!hug) 
			return super.getSize();
		return { ...hug };
	}
}
